/**
 * Created by Maxim B. on 01/04/20.
 * Copyright © 2020 SEVEN. All rights reserved.
 */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, TemplateRef, ViewChild, ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'curr-timepicker',
  templateUrl: './timepicker.component.html',
  styleUrls: ['./timepicker.component.scss']
})

export class TimepickerComponent implements OnInit, OnChanges, OnDestroy {
  private langChangeSubscription: Subscription;

  @ViewChild('customInput', { read: ViewContainerRef, static: true }) public customInput: ViewContainerRef;
  @ViewChild('confirmButton', { static: true }) confirmButtonTmpl: TemplateRef<any>;
  @ViewChild('cancelButton', { static: true }) cancelButtonTmpl: TemplateRef<any>;
  @Input() isBordered = true;
  @Input() placeholder = '';
  @Input() value: string | number;
  @Input() controlName: string;
  @Input() errorMessage = '';
  @Input() prefix = '';
  @Input() isGreyBackground = false;
  @Input() isDisabled = false;
  @Input() isReadOnly = false;
  @Input() formGroup: FormGroup;
  @Input() isCentered?: boolean;
  @Input() isRequired?: boolean;
  @Input() suffix?: string;
  @Input() isSuffixDisabled: boolean = false;
  @Input() userHint = '';
  @Output() valueChange = new EventEmitter<string | number | null>();
  @Output() onInputBlur = new EventEmitter<string | number | null>();
  @Output() onSuffixClick = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService) {
  }

  ngOnInit(): void {
    if (!this.formGroup) {
      this.controlName = 'item' + Date.now() + Math.random();
      this.formGroup = this.fb.group({
        [this.controlName]: new FormControl({ value: this.value, disabled: this.isDisabled })
      });
    }

    // Set validation rules
    if (this.isRequired) {
      this.formGroup.controls[this.controlName].setValidators([Validators.required]);
    }

    // Update timepicker locale on initialization
    this.updateTimepickerLocale();

    // Subscribe to language changes
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateTimepickerLocale();
    });
  }

  /**
   * Updates the timepicker locale when the language changes
   * This is necessary because the TIME_LOCALE provider is only evaluated once during module initialization
   */
  private updateTimepickerLocale(): void {
    // Force the timepicker to re-render with the new locale
    // We need to use setTimeout to ensure this happens in the next change detection cycle
    setTimeout(() => {
      // Get the current value
      const currentValue = this.formGroup.controls[this.controlName].value;

      // Trigger change detection by updating the form control value
      // This will cause the timepicker to re-initialize with the new locale
      this.formGroup.controls[this.controlName].setValue(null);
      this.formGroup.controls[this.controlName].setValue(currentValue);
    }, 500);
  }

  changeValue(val: any) {
    this.valueChange.emit(val);
  }

  updateValue() {
    const val = this.formGroup.controls[this.controlName].value;
    this.valueChange.emit(val);
  }

  ngOnChanges(): void {
    if (this.formGroup && this.value?.toString().length) {
      this.formGroup.controls[this.controlName].setValue(this.value);
    }
    if (this.formGroup) {
      if (this.isDisabled) {
        this.formGroup.controls[this.controlName].disable();
      } else {
        this.formGroup.controls[this.controlName].enable();
      }
    }
  }

  onLostFocus() {
    if (this.formGroup.controls[this.controlName]) {
      const val = this.formGroup.controls[this.controlName].value;
      this.onInputBlur.emit(val);
    }
  }

  suffixClicked(event): void {
    if (this.isSuffixDisabled) {
      return;
    }
    event.stopPropagation();
    this.onSuffixClick.emit();
  }

  focusInput(): void {
    this.customInput.element.nativeElement.focus();
  }

  /**
   * Clean up subscriptions when the component is destroyed
   */
  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
